import { Button as ButtonPrimitive } from '@base-ui/react/button';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../lib/cn';

const buttonVariants = cva(
  'group/button focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 inline-flex shrink-0 cursor-pointer items-center justify-center rounded-md border border-transparent bg-clip-padding font-medium whitespace-nowrap transition-all outline-none select-none focus-visible:ring-3 active:not-aria-[haspopup]:translate-y-px disabled:pointer-events-none disabled:opacity-50 aria-invalid:ring-3 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      /**
       * Rank. Colour comes from `tone`. `link` is `tertiary` that keeps an underline.
       * `rail` is a navigation-rail control: bare below `lg`, where the rail is a top bar,
       * and outlined from `lg`. It is always neutral and ignores `tone`.
       */
      variant: {
        primary: '',
        secondary: '',
        outline: '',
        tertiary: '',
        link: 'underline underline-offset-4',
        rail: 'text-button-neutral-fg hover:bg-button-neutral-quiet-hover aria-expanded:bg-button-neutral-quiet-hover lg:border-border-strong lg:bg-surface-elevated',
      },
      tone: {
        neutral: '',
        accent: '',
        warning: '',
        critical: '',
      },
      /** xs 28 px / 14 px text / 4–8 px padding / radius 4, for compact toolbars such as the recorder pill. sm 36 px / 14 px text / 12 px padding / radius 6, md 40 / 16 / 14 / 7, lg 44 / 18 / 16 / 8. Icon sizes: `icon-xs` 28 px, `icon-sm` 36, `icon` 40, `icon-lg` 44. `icon-inline` has no padding and hugs its icon, for an icon button inline with other icons or text. Nothing renders text under 14 px. */
      size: {
        xs: "h-7 gap-1 rounded-sm pr-2 pl-1 text-sm [&_svg:not([class*='size-'])]:size-4",
        sm: "h-9 px-3 text-sm has-data-[icon=inline-end]:pr-2.5 has-data-[icon=inline-start]:pl-2.5 [&_svg:not([class*='size-'])]:size-4",
        md: "h-10 rounded-[7px] px-3.5 text-base has-data-[icon=inline-end]:pr-3 has-data-[icon=inline-start]:pl-3 [&_svg:not([class*='size-'])]:size-5",
        lg: "h-11 rounded-lg px-4 text-lg has-data-[icon=inline-end]:pr-3.5 has-data-[icon=inline-start]:pl-3.5 [&_svg:not([class*='size-'])]:size-5",
        'icon-xs': "size-7 [&_svg:not([class*='size-'])]:size-4",
        'icon-sm': "size-9 [&_svg:not([class*='size-'])]:size-4",
        icon: "size-10 rounded-[7px] [&_svg:not([class*='size-'])]:size-5",
        'icon-lg': "size-11 rounded-lg [&_svg:not([class*='size-'])]:size-5",
        'icon-inline': "size-auto p-0 [&_svg:not([class*='size-'])]:size-4",
      },
    },
    compoundVariants: [
      {
        tone: 'neutral',
        variant: 'primary',
        className:
          'bg-button-neutral-primary text-button-neutral-primary-fg hover:bg-button-neutral-primary-hover aria-expanded:bg-button-neutral-primary-hover',
      },
      {
        tone: 'neutral',
        variant: 'secondary',
        className:
          'bg-button-neutral-secondary text-button-neutral-fg hover:bg-button-neutral-secondary-hover aria-expanded:bg-button-neutral-secondary-hover',
      },
      {
        tone: 'neutral',
        variant: 'outline',
        className:
          'border-button-neutral-border bg-surface-elevated text-button-neutral-fg hover:bg-button-neutral-quiet-hover aria-expanded:bg-button-neutral-quiet-hover',
      },
      {
        tone: 'neutral',
        variant: ['tertiary', 'link'],
        className:
          'text-button-neutral-fg hover:bg-button-neutral-quiet-hover aria-expanded:bg-button-neutral-quiet-hover',
      },
      {
        tone: 'accent',
        variant: 'primary',
        className:
          'bg-button-accent-primary text-button-accent-primary-fg hover:bg-button-accent-primary-hover aria-expanded:bg-button-accent-primary-hover',
      },
      {
        tone: 'accent',
        variant: 'secondary',
        className:
          'bg-button-accent-secondary text-button-accent-fg hover:bg-button-accent-secondary-hover aria-expanded:bg-button-accent-secondary-hover',
      },
      {
        tone: 'accent',
        variant: 'outline',
        className:
          'border-button-accent-border bg-surface-elevated text-button-accent-fg hover:bg-button-accent-quiet-hover aria-expanded:bg-button-accent-quiet-hover',
      },
      {
        tone: 'accent',
        variant: ['tertiary', 'link'],
        className:
          'text-button-accent-fg hover:bg-button-accent-quiet-hover aria-expanded:bg-button-accent-quiet-hover',
      },
      {
        tone: 'warning',
        variant: 'primary',
        className:
          'bg-button-warning-primary text-button-warning-primary-fg hover:bg-button-warning-primary-hover aria-expanded:bg-button-warning-primary-hover',
      },
      {
        tone: 'warning',
        variant: 'secondary',
        className:
          'bg-button-warning-secondary text-button-warning-fg hover:bg-button-warning-secondary-hover aria-expanded:bg-button-warning-secondary-hover',
      },
      {
        tone: 'warning',
        variant: 'outline',
        className:
          'border-button-warning-border bg-surface-elevated text-button-warning-fg hover:bg-button-warning-quiet-hover aria-expanded:bg-button-warning-quiet-hover',
      },
      {
        tone: 'warning',
        variant: ['tertiary', 'link'],
        className:
          'text-button-warning-fg hover:bg-button-warning-quiet-hover aria-expanded:bg-button-warning-quiet-hover',
      },
      {
        tone: 'critical',
        variant: 'primary',
        className:
          'bg-button-critical-primary text-button-critical-primary-fg hover:bg-button-critical-primary-hover aria-expanded:bg-button-critical-primary-hover',
      },
      {
        tone: 'critical',
        variant: 'secondary',
        className:
          'bg-button-critical-secondary text-button-critical-fg hover:bg-button-critical-secondary-hover aria-expanded:bg-button-critical-secondary-hover',
      },
      {
        tone: 'critical',
        variant: 'outline',
        className:
          'border-button-critical-border bg-surface-elevated text-button-critical-fg hover:bg-button-critical-quiet-hover aria-expanded:bg-button-critical-quiet-hover',
      },
      {
        tone: 'critical',
        variant: ['tertiary', 'link'],
        className:
          'text-button-critical-fg hover:bg-button-critical-quiet-hover aria-expanded:bg-button-critical-quiet-hover',
      },
    ],
    defaultVariants: {
      variant: 'primary',
      tone: 'neutral',
      size: 'sm',
    },
  },
);

interface ButtonExtraProps {
  loading?: boolean;
}

function Button({
  className,
  variant = 'primary',
  size = 'sm',
  tone = 'neutral',
  loading = false,
  disabled,
  ...props
}: ButtonPrimitive.Props & VariantProps<typeof buttonVariants> & ButtonExtraProps) {
  const isDisabled = disabled ?? loading;
  return (
    <ButtonPrimitive
      data-slot="button"
      data-component="button"
      data-state={loading ? 'loading' : isDisabled ? 'disabled' : 'idle'}
      data-variant={variant}
      data-size={size}
      data-tone={tone}
      data-loading={loading || undefined}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={cn(buttonVariants({ variant, size, tone, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
